import {useMemo, useState} from 'react';
import {Link, useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {AlertCircle, ArrowLeft, Eye, EyeOff, Lock, Mail, Send} from 'lucide-react';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Card} from '@/components/ui/card';
import {useAuth} from '@/contexts/AuthContext';
import {useBranding} from '@/contexts/BrandingContext';

const PLATFORM_LOGO = '/branding/condutor-online-logo.png';
const PLATFORM_NAME = 'condutor.online';

export const PasswordResetRequestPage = () => {
    const {requestPasswordReset, resetPassword} = useAuth();
    const {branding} = useBranding();
    const navigate = useNavigate();
    const {token: routeToken} = useParams();
    const [searchParams] = useSearchParams();
    const initialEmail = useMemo(() => searchParams.get('email') || '', [searchParams]);
    const resetToken = routeToken || searchParams.get('token') || '';
    const isPasswordResetLink = Boolean(resetToken);

    const [email, setEmail] = useState(initialEmail);
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sent, setSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!email.trim()) {
            setError('Indique o email da sua conta.');
            return;
        }

        setLoading(true);
        const result = await requestPasswordReset({email: email.trim()});
        setLoading(false);

        if (!result.ok) {
            setError(result.error);
            return;
        }

        setSent(true);
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!email.trim()) {
            setError('Indique o email da sua conta.');
            return;
        }
        if (!password || !passwordConfirmation) {
            setError('Preencha todos os campos.');
            return;
        }
        if (password !== passwordConfirmation) {
            setError('A nova palavra-passe e a confirmacao nao coincidem.');
            return;
        }
        if (password.length < 8) {
            setError('A nova palavra-passe tem de ter pelo menos 8 caracteres.');
            return;
        }

        setLoading(true);
        const result = await resetPassword({
            email: email.trim(),
            token: resetToken,
            password,
        });
        setLoading(false);

        if (!result.ok) {
            setError(result.error);
            return;
        }

        navigate('/login', {
            replace: true,
            state: {message: 'Palavra-passe reposta. Pode iniciar sessao.'},
        });
    };

    return (
        <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-6" data-testid="password-reset-request-page">
            <div className="w-full max-w-md">
                <div className="flex items-center gap-3 mb-8">
                    {branding.logo && branding.logo !== PLATFORM_LOGO ? (
                        <img src={branding.logo} alt={branding.companyName || 'Cliente'} className="h-11 w-auto"/>
                    ) : (
                        <div className="bg-black rounded px-2 py-1 ring-1 ring-slate-200">
                            <img src={PLATFORM_LOGO} alt={PLATFORM_NAME} className="h-6 w-auto block"/>
                        </div>
                    )}
                    <h1 className="font-secondary font-bold text-lg text-slate-900 flex-1">{branding.companyName || PLATFORM_NAME}</h1>
                </div>

                <Card className="p-8 shadow-2xl border-blue-100 relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-1.5" style={{backgroundColor: '#facc15'}}/>
                    <div className="mb-6">
                        <h2 className="text-2xl font-secondary font-bold text-slate-900">Repor palavra-passe</h2>
                        <p className="text-sm text-slate-500 mt-1">
                            {isPasswordResetLink
                                ? 'Defina uma nova palavra-passe para a sua conta.'
                                : 'Receba um link no seu email para definir uma nova palavra-passe.'}
                        </p>
                    </div>

                    {isPasswordResetLink ? (
                        <form onSubmit={handlePasswordSubmit} className="space-y-4" data-testid="password-reset-form">
                            <div className="space-y-2">
                                <Label className="text-sm" htmlFor="reset-email">Email</Label>
                                <div className="relative">
                                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                                    <Input
                                        id="reset-email"
                                        type="email"
                                        autoComplete="email"
                                        placeholder="seuemail@empresa.pt"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        className="pl-9"
                                        data-testid="password-reset-email"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-sm" htmlFor="reset-password">Nova palavra-passe</Label>
                                <div className="relative">
                                    <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                                    <Input
                                        id="reset-password"
                                        type={showPassword ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        className="pl-9 pr-10"
                                        data-testid="password-reset-new-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(value => !value)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                                        aria-label={showPassword ? 'Ocultar palavra-passe' : 'Mostrar palavra-passe'}
                                    >
                                        {showPassword ? <EyeOff size={15}/> : <Eye size={15}/>}
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-sm" htmlFor="reset-password-confirmation">Confirmar palavra-passe</Label>
                                <div className="relative">
                                    <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                                    <Input
                                        id="reset-password-confirmation"
                                        type={showPassword ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        value={passwordConfirmation}
                                        onChange={e => setPasswordConfirmation(e.target.value)}
                                        className="pl-9"
                                        data-testid="password-reset-confirm-password"
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
                                    <AlertCircle size={14} className="mt-0.5 flex-shrink-0"/>
                                    <span>{error}</span>
                                </div>
                            )}

                            <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-800 h-11 text-white" disabled={loading}>
                                {loading ? 'A guardar...' : <>Guardar nova palavra-passe <Send size={16} className="ml-2"/></>}
                            </Button>

                            <Button asChild variant="ghost" className="w-full">
                                <Link to="/login">
                                    <ArrowLeft size={16} className="mr-2"/>
                                    Voltar ao login
                                </Link>
                            </Button>
                        </form>
                    ) : sent ? (
                        <div className="space-y-5">
                            <div className="rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-800">
                                Se existir uma conta associada a este email, enviaremos as instrucoes de reposicao.
                            </div>
                            <Button asChild className="w-full bg-blue-700 hover:bg-blue-800 h-11 text-white">
                                <Link to="/login">
                                    <ArrowLeft size={16} className="mr-2"/>
                                    Voltar ao login
                                </Link>
                            </Button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4" data-testid="password-reset-request-form">
                            <div className="space-y-2">
                                <Label className="text-sm" htmlFor="reset-email">Email</Label>
                                <div className="relative">
                                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                                    <Input
                                        id="reset-email"
                                        type="email"
                                        autoComplete="email"
                                        placeholder="seuemail@empresa.pt"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        className="pl-9"
                                        data-testid="password-reset-email"
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
                                    <AlertCircle size={14} className="mt-0.5 flex-shrink-0"/>
                                    <span>{error}</span>
                                </div>
                            )}

                            <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-800 h-11 text-white" disabled={loading}>
                                {loading ? 'A enviar...' : <>Enviar link <Send size={16} className="ml-2"/></>}
                            </Button>

                            <Button asChild variant="ghost" className="w-full">
                                <Link to="/login">
                                    <ArrowLeft size={16} className="mr-2"/>
                                    Voltar ao login
                                </Link>
                            </Button>
                        </form>
                    )}
                </Card>
            </div>
        </div>
    );
};
